/* eslint-disable @typescript-eslint/no-explicit-any */
import { navigate } from 'gatsby'
import type { Facet } from '@faststore/sdk/dist/search/useSearchState'
import type { IStoreSelectedFacet } from '@faststore/api'
import { CloseButtonIcon } from 'src/components/Icons/CloseButtonIcon'
import { useSearch } from '@faststore/sdk'
import { useCallback, useEffect, useState } from 'react'
import classNames from 'classnames'

import { useFilter } from './useFilter'
import type { FacetValue, Facet as FacetV2 } from './types'

interface SelectedFacetsProps {
  facetsv2: FacetV2[] | undefined
}

interface RemoveFacetCustomProps {
  allFacets: IStoreSelectedFacet[]
  removalFunc: (item: any) => void
  typeOfRemoval: 'all' | 'single'
  singleFacet?: Facet
}

type FilterFacet = {
  key: string
  label: string
  quantity: number
  selected: boolean
  value: string
}

const navigateToCategory = (allFacets: any[], categoryKey: string) => {
  const category = allFacets.find((item) => item.key === categoryKey)?.value

  if (category) {
    navigate(`/${category}`)
  }
}

const handleFirstFacetRemoval = (allFacets: any[], singleFacet: any) => {
  const newUrl = window.location.pathname
  const newSearchParams = new URLSearchParams(window.location.search)
  const isUnique = allFacets.length === 1

  if (!isUnique) {
    const currentSelectedFacets = newSearchParams.getAll(
      newSearchParams.get('facets')?.split(',')[0] ?? ''
    )

    const toNavigate = `${newUrl}?${newSearchParams}`
      .toString()
      .replace(`&${singleFacet?.key}=${currentSelectedFacets[0]}`, '')

    navigate(toNavigate)

    return
  }

  isUnique && singleFacet?.key && newSearchParams.delete(singleFacet.key)
  isUnique && newSearchParams.delete('facets')
  navigate(`${newUrl}?${newSearchParams}`)
}

const handleAllRemoval = (
  allFacets: any[],
  removalFunc: any,
  state: { base: string; term: string | null }
) => {
  removalFunc({ type: 'selectFacets', payload: [] })

  if (allFacets.some((item) => item.key === 'category-1')) {
    navigateToCategory(allFacets, 'category-1')

    return undefined
  }

  if (state.term && state.base.includes('/colecao')) {
    return navigate(`${state.base}?q=${state.term}`)
  }

  return navigate(state.base)
}

const facetNamesMap = {
  'iguasport ltda': 'Promo: 5% OFF no PIX*',
}

function getFacetName(facet: FilterFacet) {
  if (facet.key === 'blueproduct') {
    return 'Melhor custo benefício'
  }

  return (
    facetNamesMap[facet.label.toLowerCase() as keyof typeof facetNamesMap] ??
    facet.label.replaceAll('-', ' ').toLowerCase()
  )
}

const SelectedFacets = ({ facetsv2 }: SelectedFacetsProps) => {
  const { selected, dispatch } = useFilter(facetsv2)
  const {
    state: { base, term },
    toggleFacet,
  } = useSearch()

  const filterFacets = useCallback(() => {
    if (!facetsv2) {
      return []
    }

    return selected
      .map(
        (filter) =>
          facetsv2
            ?.find((facet) => facet.key === filter.key)
            ?.values.find((value) => value.value === filter.value) as FacetValue
      )
      .filter((item) => item?.key !== 'category-1' && item)
  }, [facetsv2, selected])

  const [filteredFacets, setFilteredFacets] = useState<FacetValue[]>(
    filterFacets()
  )

  const removeFacetCustom = ({
    allFacets,
    removalFunc,
    typeOfRemoval,
    singleFacet,
  }: RemoveFacetCustomProps): void => {
    if (typeOfRemoval === 'all') {
      handleAllRemoval(allFacets, removalFunc, { base, term })

      return
    }

    if (typeOfRemoval === 'single') {
      if (singleFacet?.key === 'category-1' && allFacets.length === 2) {
        navigate(
          `/${allFacets.find((item) => item.key === 'category-1')?.value}`
        )

        return
      }

      if (singleFacet?.key === 'category-2') {
        const newUrl = window.location.href
          .replace(`${singleFacet.value}/`, '')
          .replace(`&category-2=${singleFacet.value}`, '')
          .replace('%2Ccategory-2', '')
          .split('/')
          .slice(3)
          .join('/')

        navigate(`/${newUrl}`)

        return
      }

      if (
        allFacets.findIndex((item) => item.value === singleFacet?.value) === 0
      ) {
        handleFirstFacetRemoval(allFacets, singleFacet)
      }
    }

    removalFunc(singleFacet)
  }

  useEffect(() => {
    setFilteredFacets(filterFacets())
  }, [filterFacets])

  return (
    <div
      className={classNames('selected-filters mb-8 md:mt-7', {
        hidden: filteredFacets.length === 0,
      })}
    >
      <div className="flex gap-2 flex-wrap">
        {filteredFacets?.map((facet: any, idx) => {
          return (
            <div
              key={`${facet.value}-${idx}`}
              className="font-inter flex w-max gap-2 px-4 border border-tertiary rounded-round items-center h-7"
            >
              <p
                className="text-restructure-primary text-xs font-medium capitalize"
                data-id={facet.key}
              >
                {getFacetName(facet)}
              </p>
              <button
                aria-label="Remove Selected Filter"
                onClick={() =>
                  removeFacetCustom({
                    allFacets: selected,
                    removalFunc: toggleFacet,
                    typeOfRemoval: 'single',
                    singleFacet: facet,
                  })
                }
              >
                <CloseButtonIcon size={13} />
              </button>
            </div>
          )
        })}
      </div>
      {filteredFacets?.length > 0 && (
        <button
          className="text-restructure-primary text-xs font-medium mt-[14px] underline underline-offset-4"
          onClick={() =>
            handleAllRemoval(
              selected,
              () => dispatch({ type: 'selectFacets', payload: [] }),
              { base, term }
            )
          }
        >
          Limpar tudo ({String(filteredFacets.length).padStart(2, '0')})
        </button>
      )}
    </div>
  )
}

export default SelectedFacets
